import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import FadeInImage from '../../FadeInImage';

/**
 * Premium blog post card — visual language matches EventCard
 * (shared/components/EventCard/index.js): rounded-2xl, border + shadow-card,
 * image zoom on hover, FadeInImage, font-display title.
 *
 * Props: `data` = a single post object from API.Posts()/API.Post()
 *   { id, title, image, description, slug, created_at, categories[], tags[] }
 *
 * Kept as a plain presentational component (no <Link> here) so callers can
 * keep wrapping it in <Link> exactly like before — no data-flow changes.
 */

const MONTH_NAMES_FR = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTH_NAMES_FR[d.getMonth()]} ${d.getFullYear()}`;
}

// Blog `description` can carry HTML (or be null on list endpoints) — strip
// tags + common entities for the card's plain-text excerpt.
function excerpt(html, max = 110) {
  if (!html) return '';
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&#8217;|&rsquo;/gi, '’')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

const Item = (props) => {
  const { t } = useTranslation();
  const post = props.data || {};
  const category = post.categories && post.categories[0];
  const desc = excerpt(post.description);

  return (
    <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card transition-all duration-300 ease-out-quint hover:-translate-y-1 hover:border-brand-100 hover:shadow-elevated">
      {/* image */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <div className="h-full w-full transition-transform duration-[900ms] ease-out-quint group-hover:scale-[1.06]">
          <FadeInImage
            src={post.image}
            alt={post.title}
            className="h-full w-full object-cover"
          />
        </div>

        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/30 to-transparent" />

        {category ? (
          <div className="absolute inset-x-3 top-3">
            <span className="inline-flex items-center rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-900 shadow-card backdrop-blur">
              {category.name}
            </span>
          </div>
        ) : null}
      </div>

      {/* body */}
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center gap-1.5 text-xs text-ink-subtle">
          <CalendarDaysIcon className="h-3.5 w-3.5 text-brand-500" />
          <span>{formatDate(post.created_at)}</span>
        </div>

        <h3 className="mt-1.5 line-clamp-2 font-display text-base font-semibold leading-snug text-ink transition-colors group-hover:text-brand-500">
          {post.title}
        </h3>

        {desc ? (
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-ink-muted">
            {desc}
          </p>
        ) : null}

        <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-3">
          <span className="text-xs font-semibold text-brand-500">
            {t('blog.readMore')}
          </span>
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500 transition-all duration-200 group-hover:bg-accent-500 group-hover:text-brand-900">
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 rtl:rotate-180" aria-hidden>
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
            </svg>
          </span>
        </div>
      </div>
    </div>
  );
};
export default Item;
